import React, { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, useParams, useSearchParams } from 'react-router-dom';
import api from './api';
import { Icons } from './Icons';
import { BoxFormPage, ItemFormPage, UsersPage, RoomsPage, SettingsPage } from './Pages';
import APP_VERSION from './version';

const ROLE_LABELS = { admin: 'Admin', helper: 'Helfer' };
const STATUS_LABELS = { open: 'Offen', packed: 'Gepackt', unpacked: 'Ausgepackt' };
const STATUS_CLASSES = { open: 'badge-open', packed: 'badge-packed', unpacked: 'badge-unpacked' };
const LOCATION_LABELS = { wuppertal: 'Wuppertal', friedemannweg: 'Friedemannweg', waldemarstrasse: 'Waldemarstraße' };
const LOCATION_CLASSES = { wuppertal: 'badge-open', friedemannweg: 'badge-packed', waldemarstrasse: 'badge-unpacked' };

// === Context ===
const AuthContext = createContext(null);
const ToastContext = createContext(null);
export function useAuth() { return useContext(AuthContext); }
export function useToast() { return useContext(ToastContext); }

// === Toast ===
function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const addToast = useCallback((msg, type = 'success') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, msg, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 4000);
  }, []);
  return (
    <ToastContext.Provider value={addToast}>
      {children}
      <div className="toast-container">
        {toasts.map(t => <div key={t.id} className={`toast toast-${t.type}`}>{t.msg}</div>)}
      </div>
    </ToastContext.Provider>
  );
}

// === Auth ===
function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('user')); } catch { return null; }
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      api.get('/auth/me').then(res => { setUser(res.data); setLoading(false); })
        .catch(() => { localStorage.clear(); setUser(null); setLoading(false); });
    } else setLoading(false);
  }, []);

  const login = async (email, password) => {
    const res = await api.post('/auth/login', { email, password });
    localStorage.setItem('token', res.data.token);
    localStorage.setItem('user', JSON.stringify(res.data.user));
    setUser(res.data.user);
  };
  const logout = () => { localStorage.clear(); setUser(null); };

  if (loading) return <div className="login-page"><div className="spinner" /></div>;
  return <AuthContext.Provider value={{ user, login, logout }}>{children}</AuthContext.Provider>;
}

function ProtectedRoute({ children, roles }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" />;
  return children;
}

// === Login ===
function LoginPage() {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault(); setError(''); setLoading(true);
    try { await login(email, password); } catch (err) { setError(err.response?.data?.error || 'Anmeldung fehlgeschlagen'); }
    finally { setLoading(false); }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-brand">
          <div className="login-brand-icon"><Icons.ErfurtRad /></div>
          <span className="login-brand-text">Umzug nach Erfurt</span>
        </div>
        <h1 className="login-title">Willkommen<br />zurück.</h1>
        <p className="login-subtitle">Melde dich an, um deinen Umzug zu verwalten.</p>
        <div className="login-card">
          {error && <div className="alert alert-error">{error}</div>}
          <form onSubmit={handleSubmit}>
            <div className="form-group"><label className="form-label">E-Mail-Adresse</label><input className="form-input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="deine@email.de" required autoFocus /></div>
            <div className="form-group"><label className="form-label">Passwort</label><input className="form-input" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Passwort" required /></div>
            <button className="btn btn-primary btn-lg" style={{ width: '100%' }} disabled={loading}>{loading ? 'Wird angemeldet…' : 'Anmelden'}</button>
          </form>
        </div>
        <p className="login-footer">Kisten-Tracker für euren Umzug</p>
      </div>
    </div>
  );
}

// === Dashboard ===
function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [showAllRooms, setShowAllRooms] = useState(false);
  const navigate = useNavigate();
  useEffect(() => { api.get('/stats').then(r => setStats(r.data)); }, []);
  if (!stats) return <div className="page-container"><div className="loading-page"><div className="spinner" /></div></div>;

  const pctPacked = stats.total ? Math.round((stats.packed / stats.total) * 100) : 0;
  const pctUnpacked = stats.total ? Math.round((stats.unpacked / stats.total) * 100) : 0;
  const iPctFriedemannweg = stats.itemsTotal ? Math.round((stats.itemsFriedemannweg / stats.itemsTotal) * 100) : 0;
  const iPctWaldemarstrasse = stats.itemsTotal ? Math.round((stats.itemsWaldemarstrasse / stats.itemsTotal) * 100) : 0;

  const totalAll = stats.total + (stats.itemsTotal || 0);
  const doneAll = stats.packed + stats.unpacked + (stats.itemsFriedemannweg || 0) + (stats.itemsWaldemarstrasse || 0);
  const pctTotal = totalAll ? Math.round((doneAll / totalAll) * 100) : 0;

  return (
    <div className="page-container">
      <div className="page-header">
        <div><h1 className="page-title">Dashboard</h1><p className="page-subtitle">Übersicht eures Umzugs nach Erfurt</p></div>
        <div className="btn-group">
          <button className="btn btn-primary" onClick={() => navigate('/boxes/new')}><Icons.Plus /> Neue Kiste</button>
          <button className="btn btn-secondary" onClick={() => navigate('/items/new')}><Icons.Plus /> Neues Einzelstück</button>
        </div>
      </div>

      {/* Fortschritts-Banner */}
      <div className="progress-banner">
        <div className="progress-banner-info">
          <h3>Gesamtfortschritt</h3>
          <p>{stats.packed} von {stats.total} Kisten gepackt{stats.itemsTotal > 0 ? `, ${stats.itemsFriedemannweg + stats.itemsWaldemarstrasse} von ${stats.itemsTotal} Einzelstücken bereit` : ''}</p>
        </div>
        <div className="progress-bar-wide"><div className="progress-fill" style={{ width: `${pctTotal}%` }} /></div>
        <div className="progress-percent">{pctTotal}%</div>
      </div>

      {/* Stats Grid */}
      <div className="stats-grid">
        <div className="stat-card" style={{ cursor: 'pointer' }} onClick={() => navigate('/boxes')}>
          <div className="stat-top"><span className="stat-label">Kisten</span><div className="stat-icon stat-icon-amber">&#128230;</div></div>
          <div className="stat-value">{stats.total}</div>
          <div className="stat-sub"><strong>{stats.packed}</strong> gepackt</div>
        </div>
        <div className="stat-card" style={{ cursor: 'pointer' }} onClick={() => navigate('/items')}>
          <div className="stat-top"><span className="stat-label">Einzelstücke</span><div className="stat-icon stat-icon-orange">&#128722;</div></div>
          <div className="stat-value">{stats.itemsTotal || 0}</div>
          <div className="stat-sub"><strong>{(stats.itemsFriedemannweg || 0) + (stats.itemsWaldemarstrasse || 0)}</strong> bereit</div>
        </div>
      </div>

      {/* Two Column: Kisten + Räume/Historie */}
      <div className="dashboard-two-col">
        {/* Letzte Kisten */}
        <div className="card" style={{ marginBottom: 0 }}>
          <div className="card-header"><h3 className="card-title">Letzte Kisten</h3><span className="card-link" onClick={() => navigate('/boxes')}>Alle anzeigen</span></div>
          {stats.recentBoxes.length === 0 ? <div className="card-body"><p className="empty-text">Noch keine Kisten</p></div> :
            stats.recentBoxes.map(box => (
              <div key={box.id} className="dashboard-list-item" onClick={() => navigate(`/boxes/${box.id}`)}>
                <span className="dashboard-box-num">{box.number}</span>
                <div className="dashboard-box-info">
                  <div className="dashboard-box-name">{box.label || `Kiste ${box.number}`}</div>
                  <div className="dashboard-box-room">{box.room_name || ''}</div>
                </div>
                <span className={`dashboard-status-dot ${box.status === 'unpacked' ? 'done' : box.status}`} />
                <span className="dashboard-status-text">{STATUS_LABELS[box.status]}</span>
              </div>
            ))
          }
        </div>

        {/* Rechte Spalte */}
        <div className="dashboard-right-col">
          {/* Räume */}
          {stats.rooms && stats.rooms.length > 0 && (() => {
            const occupied = stats.rooms.filter(r => (r.box_count || 0) > 0);
            const emptyCount = stats.rooms.length - occupied.length;
            const shown = showAllRooms ? stats.rooms : occupied;
            return (
              <div className="card" style={{ marginBottom: 0 }}>
                <div className="card-header">
                  <h3 className="card-title">Räume</h3>
                  <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
                    {emptyCount > 0 && (
                      <span className="card-link" onClick={() => setShowAllRooms(v => !v)}>
                        {showAllRooms ? `${emptyCount} leere ausblenden` : `${emptyCount} leere anzeigen`}
                      </span>
                    )}
                    <span className="card-link" onClick={() => navigate('/rooms')}>Verwalten</span>
                  </div>
                </div>
                <div className="dashboard-rooms-grid">
                  {shown.length === 0 ? (
                    <div style={{ gridColumn: '1 / -1', padding: '12px', color: 'var(--text-muted)', fontSize: '0.85rem', textAlign: 'center' }}>
                      Noch keine Kisten in Räumen
                    </div>
                  ) : shown.map(room => (
                    <div key={room.id} className={`dashboard-room-card ${(room.box_count || 0) === 0 ? 'empty' : ''}`} onClick={() => navigate(`/boxes?room=${room.id}`)}>
                      <span className="dashboard-room-name">{room.name}</span>
                      <span className="dashboard-room-count">{room.box_count || 0}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* Letzte Änderungen */}
          <div className="card" style={{ marginBottom: 0 }}>
            <div className="card-header"><h3 className="card-title">Letzte Änderungen</h3></div>
            {stats.recentBoxes.length === 0 ? <div className="card-body"><p className="empty-text">Noch keine Aktivität</p></div> :
              stats.recentBoxes.slice(0, 4).map(box => (
                <div key={box.id} className="dashboard-history-item">
                  <div className="dashboard-history-dot" style={{ background: box.status === 'packed' ? 'var(--primary)' : box.status === 'unpacked' ? 'var(--success)' : 'var(--border)' }} />
                  <div>
                    <div className="dashboard-history-text"><strong>Kiste {box.number}</strong> {box.label ? `– ${box.label}` : ''}</div>
                    <div className="dashboard-history-time">{box.updated_at ? new Date(box.updated_at).toLocaleString('de-DE') : ''}</div>
                  </div>
                </div>
              ))
            }
          </div>
        </div>
      </div>
    </div>
  );
}

// === Sort Header Helper ===
function SortHeader({ label, field, currentSort, currentOrder, onSort }) {
  const isActive = currentSort === field;
  return (
    <th className={`th-sortable ${isActive ? 'th-sort-active' : ''}`} onClick={() => onSort(field)}>
      <span className="th-sort-inner">
        {label}
        <span className="sort-icon">{isActive ? (currentOrder === 'asc' ? '▲' : '▼') : '⇅'}</span>
      </span>
    </th>
  );
}

// === Boxes List ===
function BoxesListPage() {
  const { user } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [boxes, setBoxes] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [rooms, setRooms] = useState([]);
  const [filters, setFilters] = useState({ room_id: searchParams.get('room') || '', status: '', search: '' });
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortField, setSortField] = useState('number');
  const [sortOrder, setSortOrder] = useState('asc');

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(o => o === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
    setPage(1);
  };

  const fetchBoxes = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 50, sort: sortField, order: sortOrder };
      if (filters.room_id) params.room_id = filters.room_id;
      if (filters.status) params.status = filters.status;
      if (filters.search) params.search = filters.search;
      const res = await api.get('/boxes', { params });
      setBoxes(res.data.boxes); setTotal(res.data.total); setTotalPages(res.data.totalPages);
    } catch { toast('Fehler beim Laden', 'error'); }
    finally { setLoading(false); }
  }, [page, filters, sortField, sortOrder, toast]);

  useEffect(() => { fetchBoxes(); }, [fetchBoxes]);
  useEffect(() => { api.get('/rooms').then(r => setRooms(r.data)); }, []);

  const handleStatusChange = async (box, newStatus) => {
    try {
      await api.patch(`/boxes/${box.id}/status`, { status: newStatus });
      toast(`Kiste ${box.number}: ${STATUS_LABELS[newStatus]}`);
      fetchBoxes();
    } catch { toast('Fehler', 'error'); }
  };

  const handleBulkDelete = async () => {
    if (selected.length === 0) return;
    if (!window.confirm(`${selected.length} Kiste(n) löschen?`)) return;
    try {
      await api.post('/boxes/bulk-delete', { ids: selected });
      toast(`${selected.length} Kiste(n) gelöscht`);
      setSelected([]);
      fetchBoxes();
    } catch (err) { toast(err.response?.data?.error || 'Fehler', 'error'); }
  };

  const handleExport = async (format) => {
    try {
      const params = {};
      if (filters.room_id) params.room_id = filters.room_id;
      if (filters.status) params.status = filters.status;
      const res = await api.get(`/boxes/export/${format}`, { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url;
      a.download = `umzug-kisten.${format === 'csv' ? 'csv' : 'xlsx'}`;
      a.click(); window.URL.revokeObjectURL(url);
    } catch { toast('Export fehlgeschlagen', 'error'); }
  };

  const handleExportPdf = async () => {
    try {
      const res = await api.get('/boxes/export/pdf', { responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url;
      a.download = 'umzug-packliste.pdf';
      a.click(); window.URL.revokeObjectURL(url);
    } catch { toast('PDF-Export fehlgeschlagen', 'error'); }
  };

  const toggleAll = () => {
    if (selected.length === boxes.length) setSelected([]);
    else setSelected(boxes.map(b => b.id));
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div><h1 className="page-title">Kisten</h1><p className="page-subtitle">{total} Kisten</p></div>
        <div className="btn-group">
          <button className="btn btn-primary" onClick={() => navigate('/boxes/new')}><Icons.Plus /> Neue Kiste</button>
          <button className="btn btn-secondary" onClick={handleExportPdf}><Icons.Download /> PDF</button>
          <button className="btn btn-secondary" onClick={() => handleExport('xlsx')}><Icons.Download /> Excel</button>
          <button className="btn btn-secondary" onClick={() => handleExport('csv')}><Icons.Download /> CSV</button>
        </div>
      </div>

      <div className="filters-bar">
        <input className="form-input" placeholder="Suche (Nummer, Bezeichnung, Inhalt…)" value={filters.search}
          onChange={e => { setFilters(f => ({ ...f, search: e.target.value })); setPage(1); }} />
        <select className="form-select" value={filters.room_id} onChange={e => { setFilters(f => ({ ...f, room_id: e.target.value })); setPage(1); }}>
          <option value="">Alle Räume</option>{rooms.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select>
        <select className="form-select" value={filters.status} onChange={e => { setFilters(f => ({ ...f, status: e.target.value })); setPage(1); }}>
          <option value="">Alle Status</option>
          <option value="open">Offen</option>
          <option value="packed">Gepackt</option>
          <option value="unpacked">Ausgepackt</option>
        </select>
      </div>

      {user?.role === 'admin' && selected.length > 0 && (
        <div style={{ marginBottom: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{selected.length} ausgewählt</span>
          <button className="btn btn-danger btn-sm" onClick={handleBulkDelete}><Icons.Trash /> Löschen</button>
        </div>
      )}

      {loading ? <div className="loading-page"><div className="spinner" /></div> : <>
        {/* Desktop: Tabelle */}
        <div className="table-container desktop-only">
          <table className="table"><thead><tr>
            {user?.role === 'admin' && <th style={{ width: 40 }}><input type="checkbox" checked={selected.length === boxes.length && boxes.length > 0} onChange={toggleAll} /></th>}
            <th>Foto</th>
            <SortHeader label="Kiste" field="number" currentSort={sortField} currentOrder={sortOrder} onSort={handleSort} />
            <SortHeader label="Bezeichnung" field="label" currentSort={sortField} currentOrder={sortOrder} onSort={handleSort} />
            <SortHeader label="Zielraum" field="room" currentSort={sortField} currentOrder={sortOrder} onSort={handleSort} />
            <SortHeader label="Inhalt" field="contents" currentSort={sortField} currentOrder={sortOrder} onSort={handleSort} />
            <SortHeader label="Status" field="status" currentSort={sortField} currentOrder={sortOrder} onSort={handleSort} />
          </tr></thead><tbody>
            {boxes.length === 0 ? <tr><td colSpan={7} className="empty-cell">Keine Kisten gefunden</td></tr> :
              boxes.map(box => (
                <tr key={box.id} className="table-row-clickable" onClick={() => navigate(`/boxes/${box.id}`)}>
                  {user?.role === 'admin' && <td onClick={e => e.stopPropagation()}>
                    <input type="checkbox" checked={selected.includes(box.id)} onChange={() => setSelected(s => s.includes(box.id) ? s.filter(i => i !== box.id) : [...s, box.id])} />
                  </td>}
                  <td>{box.photo ? <img src={`/api/uploads/${box.photo}`} alt="" className="item-photo-thumb" /> :
                    <div className="photo-placeholder"><Icons.Box style={{ width: 18, height: 18, color: 'var(--text-muted)' }} /></div>}</td>
                  <td style={{ fontWeight: 600, fontSize: '1.1rem' }}>{box.number}</td>
                  <td>{box.label || '–'}</td>
                  <td>{box.room_name || '–'}</td>
                  <td className="td-truncate">
                    {box.contents ? box.contents.replace(/\n/g, ', ').replace(/• /g, '') : '–'}
                  </td>
                  <td onClick={e => e.stopPropagation()}>
                    <select className={`form-select form-select-sm status-select status-${box.status}`} value={box.status}
                      onChange={e => handleStatusChange(box, e.target.value)}>
                      <option value="open">Offen</option>
                      <option value="packed">Gepackt</option>
                      <option value="unpacked">Ausgepackt</option>
                    </select>
                  </td>
                </tr>
              ))}
          </tbody></table>
        </div>

        {/* Mobile: Karten */}
        <div className="mobile-only mobile-card-list">
          {boxes.length === 0 ? <p className="empty-text">Keine Kisten gefunden</p> :
            boxes.map(box => (
              <div key={box.id} className="mobile-card" onClick={() => navigate(`/boxes/${box.id}`)}>
                <div className="mobile-card-left">
                  {box.photo ? <img src={`/api/uploads/${box.photo}`} alt="" className="mobile-card-thumb" /> :
                    <div className={box.number?.startsWith('B') ? 'placeholder-thumb-box-b' : 'placeholder-thumb-box'}>{box.number}</div>}
                </div>
                <div className="mobile-card-body">
                  <div className="mobile-card-top">
                    <span className="mobile-card-number">{box.number}</span>
                    <span className={`badge ${STATUS_CLASSES[box.status]}`}>{STATUS_LABELS[box.status]}</span>
                  </div>
                  <div className="mobile-card-title">{box.label || '–'}</div>
                  <div className="mobile-card-sub">{box.room_name || 'Kein Raum'}</div>
                </div>
              </div>
            ))
          }
        </div>
      </>}

      {totalPages > 1 && <div className="pagination">
        <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}><Icons.ChevronLeft style={{ width: 14, height: 14 }} /></button>
        {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
          let p; if (totalPages <= 7) p = i + 1; else if (page <= 4) p = i + 1; else if (page >= totalPages - 3) p = totalPages - 6 + i; else p = page - 3 + i;
          return <button key={p} className={p === page ? 'active' : ''} onClick={() => setPage(p)}>{p}</button>;
        })}
        <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}><Icons.ChevronRight style={{ width: 14, height: 14 }} /></button>
        <span className="pagination-info">{total} Kisten</span>
      </div>}
    </div>
  );
}

// === Box Detail ===
function BoxDetailPage() {
  const { user } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const { id } = useParams();
  const [box, setBox] = useState(null);
  const [tab, setTab] = useState('details');
  const [history, setHistory] = useState([]);

  useEffect(() => {
    api.get(`/boxes/${id}`).then(r => setBox(r.data)).catch(() => navigate('/boxes'));
    api.get(`/boxes/${id}/history`).then(r => setHistory(r.data));
  }, [id, navigate]);

  if (!box) return <div className="page-container"><div className="loading-page"><div className="spinner" /></div></div>;

  const handleStatusChange = async (newStatus) => {
    try {
      const res = await api.patch(`/boxes/${id}/status`, { status: newStatus });
      setBox(res.data);
      toast(`Status: ${STATUS_LABELS[newStatus]}`);
      api.get(`/boxes/${id}/history`).then(r => setHistory(r.data));
    } catch { toast('Fehler', 'error'); }
  };

  const handleDelete = async () => {
    if (!window.confirm('Kiste wirklich löschen?')) return;
    try { await api.delete(`/boxes/${id}`); toast('Kiste gelöscht'); navigate('/boxes'); }
    catch (err) { toast(err.response?.data?.error || 'Fehler', 'error'); }
  };

  const handleRotatePhoto = async () => {
    try {
      const res = await api.patch(`/boxes/${id}/rotate-photo`);
      setBox(res.data);
      toast('Foto gedreht');
    } catch { toast('Fehler beim Drehen', 'error'); }
  };

  const statusFlow = ['open', 'packed', 'unpacked'];
  const currentIdx = statusFlow.indexOf(box.status);
  const nextStatus = currentIdx < statusFlow.length - 1 ? statusFlow[currentIdx + 1] : null;
  const nextActionLabel = nextStatus === 'packed' ? 'Als gepackt markieren' : nextStatus === 'unpacked' ? 'Als ausgepackt markieren' : null;

  return (
    <div className="page-container">
      <button className="btn btn-ghost btn-sm" onClick={() => navigate('/boxes')} style={{ marginBottom: 8 }}><Icons.ChevronLeft /> Zurück</button>
      <div className="page-header">
        <div>
          <h1 className="page-title">Kiste {box.number}{box.label ? ` – ${box.label}` : ''}</h1>
          <p className="page-subtitle">{box.room_name || 'Kein Zielraum'}</p>
        </div>
        <div className="btn-group">
          {nextStatus && <button className="btn btn-primary" onClick={() => handleStatusChange(nextStatus)}>
            {nextStatus === 'packed' ? <Icons.Package /> : <Icons.Check />} {nextActionLabel}
          </button>}
          <button className="btn btn-secondary" onClick={() => navigate(`/boxes/${id}/edit`)}><Icons.Edit /> Bearbeiten</button>
          {user?.role === 'admin' && <button className="btn btn-danger" onClick={handleDelete}><Icons.Trash /> Löschen</button>}
        </div>
      </div>

      <div className="detail-tabs">
        <button className={`tab ${tab === 'details' ? 'active' : ''}`} onClick={() => setTab('details')}>Details</button>
        <button className={`tab ${tab === 'history' ? 'active' : ''}`} onClick={() => setTab('history')}>Historie ({history.length})</button>
      </div>

      {tab === 'details' && (
        <div className="detail-content">
          {box.photo ? (
            <div className="detail-photo-wrap">
              <img src={`/api/uploads/${box.photo}?t=${Date.now()}`} alt={`Kiste ${box.number}`} className="detail-photo-full" />
              <button className="btn btn-ghost btn-sm photo-rotate-btn" onClick={handleRotatePhoto}><Icons.RotateCw /> Drehen</button>
            </div>
          ) : (
            <button
              type="button"
              className={`detail-photo-empty ${box.number?.startsWith('B') ? 'detail-photo-empty-b' : ''}`}
              onClick={() => navigate(`/boxes/${id}/edit`)}
              title="Foto hinzufügen"
            >
              <span className="detail-photo-empty-num">{box.number}</span>
              <span className="detail-photo-empty-cta"><Icons.Camera /> Foto hinzufügen</span>
            </button>
          )}
          <div className="detail-field"><div className="detail-label">Status</div>
            <div className="detail-value">
              <div className="status-buttons">
                {statusFlow.map(s => (
                  <button key={s} className={`btn btn-sm ${box.status === s ? `btn-status-${s}` : 'btn-ghost'}`}
                    onClick={() => handleStatusChange(s)}>{STATUS_LABELS[s]}</button>
                ))}
              </div>
            </div>
          </div>
          <div className="detail-field"><div className="detail-label">Zielraum</div><div className="detail-value">{box.room_name || '–'}</div></div>
          {box.note && <div className="detail-field"><div className="detail-label">Notiz</div><div className="detail-value">{box.note}</div></div>}
          <div className="detail-field">
            <div className="detail-label">Inhalt</div>
            <div className="detail-value">
              {box.contents ? (
                <div className="contents-list">
                  {box.contents.split('\n').map((line, i) => (
                    <div key={i} className="contents-line">{line}</div>
                  ))}
                </div>
              ) : <span style={{ color: 'var(--text-muted)' }}>Noch kein Inhalt erfasst</span>}
            </div>
          </div>
        </div>
      )}

      {tab === 'history' && (
        <div className="detail-content">
          {history.length === 0 ? <p className="empty-text">Keine Historie vorhanden</p> :
            <ul className="history-list">{history.map(h => (
              <li key={h.id} className="history-item"><div className="history-dot" /><div>
                <div><span className="history-user">{h.user_name}</span> – {h.action === 'created' ? 'Erstellt' : h.field_name && `${h.field_name} geändert`}</div>
                {h.old_value && h.new_value && <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                  <span style={{ textDecoration: 'line-through', opacity: 0.5 }}>{h.old_value}</span> → {h.new_value}
                </div>}
                {!h.old_value && h.new_value && <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{h.new_value}</div>}
                <div className="history-time">{new Date(h.created_at).toLocaleString('de-DE')}</div>
              </div></li>
            ))}</ul>}
        </div>
      )}
    </div>
  );
}

// === Global Change History (Admin) ===
function HistoryPage({ toast }) {
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/boxes/history/all', { params: { page, limit: 100 } });
      setHistory(res.data.history); setTotal(res.data.total); setTotalPages(res.data.totalPages);
    } catch { toast('Fehler beim Laden', 'error'); }
    finally { setLoading(false); }
  }, [page, toast]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  return (
    <div className="page-container">
      <div className="page-header"><div><h1 className="page-title">Änderungshistorie</h1><p className="page-subtitle">{total} Einträge</p></div></div>
      {loading ? <div className="loading-page"><div className="spinner" /></div> : <>
        <div className="table-container desktop-only">
          <table className="table"><thead><tr>
            <th>Zeitpunkt</th><th>Benutzer</th><th>Kiste</th><th>Aktion</th><th>Details</th>
          </tr></thead><tbody>
            {history.length === 0 ? <tr><td colSpan={5} className="empty-cell">Keine Einträge</td></tr>
              : history.map(h => (
                <tr key={h.id} className="table-row-clickable" onClick={() => h.box_id && navigate(`/boxes/${h.box_id}`)}>
                  <td style={{ whiteSpace: 'nowrap', fontSize: '0.85rem' }}>{new Date(h.created_at).toLocaleString('de-DE')}</td>
                  <td style={{ fontWeight: 500 }}>{h.user_name || '–'}</td>
                  <td><span style={{ color: 'var(--primary)' }}>Kiste {h.box_number || `#${h.box_id}`}{h.box_label ? ` – ${h.box_label}` : ''}</span></td>
                  <td>{h.action === 'created' ? <span className="badge badge-packed">Erstellt</span> : h.field_name || h.action}</td>
                  <td style={{ fontSize: '0.85rem' }}>
                    {h.action === 'created' ? h.new_value :
                      h.old_value && h.new_value ? <span><span style={{ textDecoration: 'line-through', opacity: 0.5 }}>{h.old_value}</span> → {h.new_value}</span> :
                        h.new_value || '–'}
                  </td>
                </tr>
              ))}
          </tbody></table>
        </div>

        <div className="mobile-only history-card-list">
          {history.length === 0 ? <div className="empty-text">Keine Einträge</div>
            : history.map(h => (
              <div key={h.id} className="history-card" onClick={() => h.box_id && navigate(`/boxes/${h.box_id}`)} style={{ cursor: h.box_id ? 'pointer' : 'default' }}>
                <div className="history-card-head">
                  <span className="history-card-time">{new Date(h.created_at).toLocaleString('de-DE')}</span>
                  <span className="history-card-actor">{h.user_name || '–'}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ color: 'var(--primary)', fontWeight: 600, fontSize: '0.88rem' }}>
                    Kiste {h.box_number || `#${h.box_id}`}{h.box_label ? ` – ${h.box_label}` : ''}
                  </span>
                  {h.action === 'created'
                    ? <span className="badge badge-packed">Erstellt</span>
                    : <span className="history-card-type">{h.field_name || h.action}</span>}
                </div>
                <div className="history-card-body">
                  {h.action === 'created' ? h.new_value :
                    h.old_value && h.new_value ? <span><span style={{ textDecoration: 'line-through', opacity: 0.5 }}>{h.old_value}</span> → {h.new_value}</span> :
                      h.new_value || '–'}
                </div>
              </div>
            ))}
        </div>

        {totalPages > 1 && <div className="pagination">
          <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}><Icons.ChevronLeft style={{ width: 14, height: 14 }} /></button>
          {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
            let p; if (totalPages <= 7) p = i + 1; else if (page <= 4) p = i + 1; else if (page >= totalPages - 3) p = totalPages - 6 + i; else p = page - 3 + i;
            return <button key={p} className={p === page ? 'active' : ''} onClick={() => setPage(p)}>{p}</button>;
          })}
          <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}><Icons.ChevronRight style={{ width: 14, height: 14 }} /></button>
          <span className="pagination-info">{total} Einträge</span>
        </div>}
      </>}
    </div>
  );
}

// === Items List (Einzelstücke) ===
function ItemsListPage() {
  const { user } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [rooms, setRooms] = useState([]);
  const [filters, setFilters] = useState({ room_id: searchParams.get('room') || '', location: '', search: '' });
  const [loading, setLoading] = useState(true);
  const [sortField, setSortField] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(o => o === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100, sort: sortField, order: sortOrder };
      if (filters.room_id) params.room_id = filters.room_id;
      if (filters.location) params.location = filters.location;
      if (filters.search) params.search = filters.search;
      const res = await api.get('/items', { params });
      setItems(res.data.items); setTotal(res.data.total);
    } catch (err) { toast('Fehler beim Laden', 'error'); }
    setLoading(false);
  }, [filters, sortField, sortOrder, toast]);

  useEffect(() => { fetchItems(); }, [fetchItems]);
  useEffect(() => { api.get('/rooms').then(r => setRooms(r.data)); }, []);

  const handleLocationChange = async (id, location) => {
    try {
      await api.patch(`/items/${id}/location`, { location });
      fetchItems(); toast(`Standort → ${LOCATION_LABELS[location]}`);
    } catch (err) { toast('Fehler', 'error'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Einzelstück wirklich löschen?')) return;
    try { await api.delete(`/items/${id}`); fetchItems(); toast('Gelöscht'); }
    catch (err) { toast('Fehler', 'error'); }
  };

  const handleExport = async (format) => {
    try {
      const params = {};
      if (filters.room_id) params.room_id = filters.room_id;
      if (filters.location) params.location = filters.location;
      const res = await api.get(`/items/export/${format}`, { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url;
      a.download = `umzug-einzelstuecke.${format === 'csv' ? 'csv' : 'xlsx'}`;
      a.click(); window.URL.revokeObjectURL(url);
    } catch { toast('Export fehlgeschlagen', 'error'); }
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div><h1 className="page-title">Einzelstücke</h1><div className="page-subtitle">{total} Einzelstücke</div></div>
        <div className="btn-group">
          <button className="btn btn-primary" onClick={() => navigate('/items/new')}><Icons.Plus /> Neues Einzelstück</button>
          <button className="btn btn-ghost" onClick={() => handleExport('xlsx')}><Icons.Download /> Excel</button>
          <button className="btn btn-ghost" onClick={() => handleExport('csv')}><Icons.Download /> CSV</button>
        </div>
      </div>

      <div className="filters-bar">
        <div className="search-input-wrapper"><Icons.Search /><input className="form-input" placeholder="Suchen…" value={filters.search} onChange={e => setFilters(f => ({ ...f, search: e.target.value }))} /></div>
        <select className="form-select" value={filters.room_id} onChange={e => setFilters(f => ({ ...f, room_id: e.target.value }))}>
          <option value="">Alle Räume</option>{rooms.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select>
        <select className="form-select" value={filters.location} onChange={e => setFilters(f => ({ ...f, location: e.target.value }))}>
          <option value="">Alle Standorte</option><option value="wuppertal">Wuppertal</option><option value="friedemannweg">Friedemannweg</option><option value="waldemarstrasse">Waldemarstraße</option>
        </select>
      </div>

      {loading ? <div className="loading-page"><div className="spinner" /></div> : <>
        {/* Desktop: Tabelle */}
        <div className="table-container desktop-only"><table className="table"><thead><tr>
          <th></th>
          <SortHeader label="Name" field="name" currentSort={sortField} currentOrder={sortOrder} onSort={handleSort} />
          <SortHeader label="Zielraum" field="room" currentSort={sortField} currentOrder={sortOrder} onSort={handleSort} />
          <SortHeader label="Standort" field="location" currentSort={sortField} currentOrder={sortOrder} onSort={handleSort} />
          {user?.role === 'admin' && <th style={{ width: 80 }}></th>}
        </tr></thead><tbody>
          {items.length === 0 ? <tr><td colSpan="5" className="empty-cell">Noch keine Einzelstücke erfasst</td></tr> :
            items.map(item => (
              <tr key={item.id} className="table-row-clickable" onClick={(e) => { if (e.target.tagName === 'SELECT' || e.target.tagName === 'BUTTON') return; navigate(`/items/${item.id}`); }}>
                <td style={{ width: 50 }}>{item.photo ? <img src={`/api/uploads/${item.photo}`} alt="" className="item-photo-thumb" /> : <div className="photo-placeholder"><Icons.Layers /></div>}</td>
                <td style={{ fontWeight: 500 }}>{item.name}</td>
                <td>{item.room_name || '–'}</td>
                <td><select className={`form-select status-select ${LOCATION_CLASSES[item.location]}`} value={item.location}
                  onClick={e => e.stopPropagation()} onChange={e => handleLocationChange(item.id, e.target.value)}>
                  <option value="wuppertal">Wuppertal</option><option value="friedemannweg">Friedemannweg</option><option value="waldemarstrasse">Waldemarstraße</option>
                </select></td>
                {user?.role === 'admin' && <td><button className="btn btn-ghost btn-sm" onClick={(e) => { e.stopPropagation(); handleDelete(item.id); }} style={{ color: 'var(--danger)' }}><Icons.Trash /></button></td>}
              </tr>
            ))
          }
        </tbody></table></div>

        {/* Mobile: Karten */}
        <div className="mobile-only mobile-card-list">
          {items.length === 0 ? <p className="empty-text">Noch keine Einzelstücke erfasst</p> :
            items.map(item => (
              <div key={item.id} className="mobile-card" onClick={() => navigate(`/items/${item.id}`)}>
                <div className="mobile-card-left">
                  {item.photo ? <img src={`/api/uploads/${item.photo}`} alt="" className="mobile-card-thumb" /> :
                    <div className="placeholder-thumb-item">{item.name?.[0]?.toUpperCase() || '?'}</div>}
                </div>
                <div className="mobile-card-body">
                  <div className="mobile-card-title">{item.name}</div>
                  <div className="mobile-card-sub">{item.room_name || 'Kein Raum'}</div>
                  <span className={`badge ${LOCATION_CLASSES[item.location]}`}>{LOCATION_LABELS[item.location]}</span>
                </div>
              </div>
            ))
          }
        </div>
      </>}
    </div>
  );
}

// === Item Detail ===
function ItemDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  const [item, setItem] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchItem = useCallback(async () => {
    try {
      const res = await api.get(`/items/${id}`);
      setItem(res.data);
      const hRes = await api.get(`/items/${id}/history`);
      setHistory(hRes.data);
    } catch (err) { toast('Fehler beim Laden', 'error'); navigate('/items'); }
    setLoading(false);
  }, [id, navigate, toast]);

  useEffect(() => { fetchItem(); }, [fetchItem]);

  const handleLocation = async (location) => {
    try { await api.patch(`/items/${id}/location`, { location }); toast(`Standort → ${LOCATION_LABELS[location]}`); fetchItem(); }
    catch (err) { toast('Fehler', 'error'); }
  };

  const handleRotatePhoto = async () => {
    try {
      const res = await api.patch(`/items/${id}/rotate-photo`);
      setItem(res.data);
      toast('Foto gedreht');
    } catch { toast('Fehler beim Drehen', 'error'); }
  };

  if (loading) return <div className="loading-page"><div className="spinner" /></div>;
  if (!item) return null;

  return (
    <div className="page-container">
      <button className="btn btn-ghost btn-sm" onClick={() => navigate('/items')} style={{ marginBottom: 8 }}><Icons.ChevronLeft /> Zurück</button>
      <div className="page-header">
        <h1 className="page-title">{item.name}</h1>
        <div className="btn-group">
          <button className="btn btn-secondary" onClick={() => navigate(`/items/${id}/edit`)}><Icons.Edit /> Bearbeiten</button>
          {user?.role === 'admin' && <button className="btn btn-danger" onClick={async () => { if (window.confirm('Einzelstück löschen?')) { await api.delete(`/items/${id}`); toast('Gelöscht'); navigate('/items'); } }}><Icons.Trash /> Löschen</button>}
        </div>
      </div>

      <div className="status-buttons" style={{ marginBottom: 24 }}>
        {['wuppertal', 'friedemannweg', 'waldemarstrasse'].map(loc => (
          <button key={loc} className={`btn ${item.location === loc ? 'btn-primary' : 'btn-ghost'}`}
            style={item.location !== loc ? { opacity: 0.5 } : {}}
            onClick={() => handleLocation(loc)}><Icons.MapPin /> {LOCATION_LABELS[loc]}</button>
        ))}
      </div>

      {item.photo ? (
        <div className="detail-photo-wrap">
          <img src={`/api/uploads/${item.photo}?t=${Date.now()}`} alt="" className="detail-photo-full" />
          <button className="btn btn-ghost btn-sm photo-rotate-btn" onClick={handleRotatePhoto}><Icons.RotateCw /> Drehen</button>
        </div>
      ) : (
        <div className="placeholder-text-item">{item.name?.[0]?.toUpperCase() || '?'}</div>
      )}
      <div className="detail-field"><div className="detail-label">Standort</div><div className="detail-value"><span className={`badge ${LOCATION_CLASSES[item.location]}`}>{LOCATION_LABELS[item.location]}</span></div></div>
      <div className="detail-field"><div className="detail-label">Zielraum</div><div className="detail-value">{item.room_name || '–'}</div></div>
      {item.note && <div className="detail-field"><div className="detail-label">Notiz</div><div className="detail-value">{item.note}</div></div>}
      <div className="detail-field"><div className="detail-label">Erstellt</div><div className="detail-value">{new Date(item.created_at + 'Z').toLocaleString('de-DE')}</div></div>
      <div className="detail-field"><div className="detail-label">Zuletzt geändert</div><div className="detail-value">{new Date(item.updated_at + 'Z').toLocaleString('de-DE')}</div></div>

      {history.length > 0 && (
        <div style={{ marginTop: 32 }}>
          <h3 style={{ marginBottom: 16, fontSize: '1rem', fontWeight: 600 }}>Änderungsverlauf</h3>
          <ul className="history-list">
            {history.map(h => (
              <li key={h.id} className="history-item"><div className="history-dot" /><div>
                <span className="history-user">{h.user_name}</span>{' '}
                {h.action === 'created' ? 'hat das Einzelstück erstellt' :
                  <span>hat <strong>{h.field_name}</strong> geändert: {h.old_value} → {h.new_value}</span>}
                <div className="history-time">{new Date(h.created_at + 'Z').toLocaleString('de-DE')}</div>
              </div></li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// === Helper Page (public, no auth) ===
function HelperPage() {
  const [boxes, setBoxes] = useState([]);
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState('kisten');
  const [search, setSearch] = useState('');
  const [detail, setDetail] = useState(null);
  const [detailType, setDetailType] = useState(null);

  useEffect(() => {
    fetch('/api/helper/boxes').then(r => r.json()).then(setBoxes).catch(() => {});
    fetch('/api/helper/items').then(r => r.json()).then(setItems).catch(() => {});
  }, []);

  const filteredBoxes = boxes.filter(b => {
    const s = search.toLowerCase();
    return !s || b.number.toLowerCase().includes(s) || (b.label || '').toLowerCase().includes(s) || (b.room_name || '').toLowerCase().includes(s);
  });

  const filteredItems = items.filter(i => {
    const s = search.toLowerCase();
    return !s || i.name.toLowerCase().includes(s) || (i.room_name || '').toLowerCase().includes(s);
  });

  const openDetail = (item, type) => { setDetail(item); setDetailType(type); };
  const closeDetail = () => { setDetail(null); setDetailType(null); };

  if (detail) {
    return (
      <div className="helper-page">
        <div className="helper-detail-header">
          <button className="helper-detail-back" onClick={closeDetail}>&#8249;</button>
          <span className="helper-detail-title">{detailType === 'box' ? `Kiste ${detail.number}` : detail.name}</span>
        </div>
        <div className="helper-room-hero">
          <div className="helper-room-label">Bitte hierhin bringen</div>
          <div className="helper-room-name">{detail.room_name || 'Kein Raum'}</div>
        </div>
        <div className="helper-detail-content">
          {detail.photo
            ? <img src={`/api/uploads/${detail.photo}`} alt="" className="helper-detail-photo" />
            : <div className="helper-detail-photo-empty">Kein Foto</div>
          }
          <div className="helper-detail-info">
            {detailType === 'box' && <>
              <div className="helper-detail-row"><span className="helper-detail-label">Bezeichnung</span><span className="helper-detail-value">{detail.label || '–'}</span></div>
              <div className="helper-detail-row"><span className="helper-detail-label">Kiste</span><span className="helper-detail-value">{detail.number}</span></div>
              <div className="helper-detail-row"><span className="helper-detail-label">Status</span><span className={`badge ${STATUS_CLASSES[detail.status]}`}>{STATUS_LABELS[detail.status]}</span></div>
            </>}
            {detailType === 'item' && <>
              <div className="helper-detail-row"><span className="helper-detail-label">Name</span><span className="helper-detail-value">{detail.name}</span></div>
              <div className="helper-detail-row"><span className="helper-detail-label">Standort</span><span className={`badge ${LOCATION_CLASSES[detail.location]}`}>{LOCATION_LABELS[detail.location]}</span></div>
            </>}
            {detail.note && <div className="helper-detail-row"><span className="helper-detail-label">Notiz</span><span className="helper-detail-value">{detail.note}</span></div>}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="helper-page">
      <div className="helper-topbar">
        <div className="helper-brand">
          <Icons.ErfurtRad style={{ width: 24, height: 24 }} />
          <span>Umzug</span>
          <span className="helper-tag">Helfer</span>
        </div>
        <div className="helper-search-wrap">
          <Icons.Search style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', width: 16, height: 16, color: 'var(--text-muted)' }} />
          <input className="helper-search" placeholder="Suchen…" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
      </div>
      <div className="helper-tabs">
        <button className={`helper-tab ${tab === 'kisten' ? 'active' : ''}`} onClick={() => setTab('kisten')}>Kisten <span className="helper-tab-count">{boxes.length}</span></button>
        <button className={`helper-tab ${tab === 'einzelstuecke' ? 'active' : ''}`} onClick={() => setTab('einzelstuecke')}>Einzelstücke <span className="helper-tab-count">{items.length}</span></button>
      </div>
      {tab === 'kisten' && (
        <div className="helper-grid-wrap">
          {filteredBoxes.length === 0 ? <p className="empty-text">Keine Kisten gefunden</p> :
            <div className="helper-box-grid">
              {filteredBoxes.map(box => (
                <div key={box.id} className="helper-box-tile" onClick={() => openDetail(box, 'box')}>
                  {box.photo
                    ? <img src={`/api/uploads/${box.photo}`} alt="" className="helper-tile-photo" />
                    : <div className="helper-tile-photo-empty"><Icons.Box style={{ width: 18, height: 18 }} /></div>
                  }
                  <div className="helper-tile-info">
                    <div className="helper-tile-number">{box.number}{box.label ? ` – ${box.label}` : ''}</div>
                    <div className="helper-tile-room">{box.room_name || '–'}</div>
                  </div>
                  <span className={`helper-tile-status ${box.status === 'unpacked' ? 'done' : box.status}`}>
                    <span className="helper-tile-status-dot" />
                    {STATUS_LABELS[box.status]}
                  </span>
                </div>
              ))}
            </div>
          }
        </div>
      )}
      {tab === 'einzelstuecke' && (
        <div className="helper-grid-wrap">
          {filteredItems.length === 0 ? <p className="empty-text">Keine Einzelstücke gefunden</p> :
            <div className="helper-item-grid">
              {filteredItems.map(item => (
                <div key={item.id} className="helper-item-tile" onClick={() => openDetail(item, 'item')}>
                  {item.photo
                    ? <img src={`/api/uploads/${item.photo}`} alt="" className="helper-item-thumb" />
                    : <div className="helper-item-thumb-empty"><Icons.Layers style={{ width: 20, height: 20 }} /></div>
                  }
                  <div className="helper-item-info">
                    <div className="helper-item-name">{item.name}</div>
                    <div className="helper-item-room">{item.room_name || '–'}</div>
                  </div>
                </div>
              ))}
            </div>
          }
        </div>
      )}
    </div>
  );
}

// === Wired Page Wrappers ===
function WiredBoxForm() { const toast = useToast(); return <BoxFormPage toast={toast} />; }
function WiredItemForm() { const toast = useToast(); return <ItemFormPage toast={toast} />; }
function WiredUsers() { const toast = useToast(); return <UsersPage toast={toast} />; }
function WiredRooms() { const toast = useToast(); return <RoomsPage toast={toast} />; }
function WiredSettings() { const toast = useToast(); return <SettingsPage toast={toast} />; }
function WiredHistory() { const toast = useToast(); return <HistoryPage toast={toast} />; }

// === Layout ===
function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navItems = [
    { path: '/', label: 'Dashboard', icon: Icons.Dashboard },
    { path: '/boxes', label: 'Kisten', icon: Icons.Package },
    { path: '/items', label: 'Einzelstücke', icon: Icons.Layers },
  ];
  const adminItems = [
    { path: '/users', label: 'Benutzer', icon: Icons.Users },
    { path: '/rooms', label: 'Räume', icon: Icons.Home },
    { path: '/history', label: 'Historie', icon: Icons.Clock },
  ];
  const isActive = (path) => path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  return (
    <div className="app-layout">
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header"><div className="sidebar-logo"><div className="logo-icon"><Icons.ErfurtRad /></div> Umzug</div></div>
        <nav className="sidebar-nav">
          {navItems.map(item => (
            <button key={item.path} className={`nav-item ${isActive(item.path) ? 'active' : ''}`} onClick={() => { navigate(item.path); setSidebarOpen(false); }}><item.icon /> {item.label}</button>
          ))}
          {user?.role === 'admin' && <>
            <div className="nav-section-title">Verwaltung</div>
            {adminItems.map(item => (
              <button key={item.path} className={`nav-item ${isActive(item.path) ? 'active' : ''}`} onClick={() => { navigate(item.path); setSidebarOpen(false); }}><item.icon /> {item.label}</button>
            ))}
          </>}
        </nav>
        <div className="sidebar-footer">
          <div className="user-info">
            <div className="user-avatar">{user?.name?.[0]?.toUpperCase()}</div>
            <div className="user-details"><div className="user-name">{user?.name}</div><div className="user-role">{ROLE_LABELS[user?.role]}</div></div>
          </div>
          <div className="user-actions">
            <button onClick={() => { navigate('/settings'); setSidebarOpen(false); }}>Passwort</button>
            <button onClick={logout}>Abmelden</button>
          </div>
        </div>
      </aside>
      <main className="main-content" onClick={() => sidebarOpen && setSidebarOpen(false)}>
        <div className="mobile-topbar">
          <button className="mobile-toggle" onClick={(e) => { e.stopPropagation(); setSidebarOpen(!sidebarOpen); }}>{sidebarOpen ? <Icons.X /> : <Icons.Menu />}</button>
          <span className="mobile-topbar-title">Umzug nach Erfurt</span>
        </div>
        <Routes>
          <Route path="/" element={<DashboardPage />} />
          <Route path="/boxes" element={<BoxesListPage />} />
          <Route path="/boxes/new" element={<WiredBoxForm />} />
          <Route path="/boxes/:id" element={<BoxDetailPage />} />
          <Route path="/boxes/:id/edit" element={<WiredBoxForm />} />
          <Route path="/items" element={<ItemsListPage />} />
          <Route path="/items/new" element={<WiredItemForm />} />
          <Route path="/items/:id" element={<ItemDetailPage />} />
          <Route path="/items/:id/edit" element={<WiredItemForm />} />
          <Route path="/users" element={<ProtectedRoute roles={['admin']}><WiredUsers /></ProtectedRoute>} />
          <Route path="/rooms" element={<ProtectedRoute roles={['admin']}><WiredRooms /></ProtectedRoute>} />
          <Route path="/history" element={<ProtectedRoute roles={['admin']}><WiredHistory /></ProtectedRoute>} />
          <Route path="/settings" element={<WiredSettings />} />
        </Routes>
      </main>
    </div>
  );
}

// === App ===
export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ToastProvider>
          <AppRoutes />
        </ToastProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

function AppRoutes() {
  const { user } = useAuth();
  return (
    <Routes>
      <Route path="/helfer" element={<HelperPage />} />
      <Route path="/login" element={user ? <Navigate to="/" /> : <LoginPage />} />
      <Route path="/*" element={user ? <Layout /> : <Navigate to="/login" />} />
    </Routes>
  );
}
